import React, { useState, useMemo, useEffect, useRef } from 'react';

/**
 * currentRegNumber + myElevators から表示テキストを導出する純関数。
 * マイリストに名前付きで登録されていれば名前、なければ登録番号、
 * どちらもなければ空文字を返す。
 */
function resolveDisplayText(regNumber, myElevators) {
  if (!regNumber) return '';
  const matched = myElevators.find(e => e.regNumber === regNumber);
  return matched ? matched.name : regNumber;
}

const Header = ({
  activeOverlay,
  setActiveOverlay,
  myElevators,
  onSelectElevator,
  currentRegNumber,
  currentElevatorData,
}) => {
  // ── 編集中の入力値（編集中のみ使用） ──
  const [isEditing, setIsEditing] = useState(false);
  const [editingInput, setEditingInput] = useState('');

  // ── レイアウト固定用 ──
  const [fixedDimensions, setFixedDimensions] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // ── 表示値: currentRegNumber + myElevators から常に自動計算 ──
  // state ではないためラグ・競合が発生しない
  const displayText = useMemo(
    () => resolveDisplayText(currentRegNumber, myElevators),
    [currentRegNumber, myElevators]
  );

  // ── currentRegNumber が変わったら編集モードを解除 ──
  useEffect(() => {
    setIsEditing(false);
  }, [currentRegNumber]);

  // 初回マウント時にサイズを固定
  useEffect(() => {
    if (containerRef.current && !fixedDimensions) {
      const rect = containerRef.current.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(containerRef.current);

      setFixedDimensions({
        height: rect.height,
        top: rect.top,
        left: rect.left,
        marginTop: computedStyle.marginTop
      });
    }
  }, [fixedDimensions]);

  // ── イベントハンドラ ──

  const handleInputChange = (e) => {
    const value = e.target.value;

    // 数字のみを許可（最大7桁）
    const numericValue = value.replace(/\D/g, '').slice(0, 7);
    setEditingInput(numericValue);

    // 7桁の数字が入力された場合のみ処理
    if (numericValue.length === 7) {
      // isEditing は true のまま維持 — fetch 完了後に currentRegNumber が
      // 変わると useEffect で自動的に false になる。
      // これにより fetch 中は入力した番号("1234567")が表示され続け、
      // 前のエレベータ名が一瞬出る問題を防ぐ。
      onSelectElevator(numericValue);

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();

    // inputクリックの場合は何もしない
    if (e.target.tagName === 'INPUT') {
      return;
    }

    // LocationInfoOverlayを開く/閉じる
    setActiveOverlay(activeOverlay === 'location' ? null : 'location');
  };

  const handleInputClick = (e) => {
    e.stopPropagation();

    // 編集モードに入り、オーバーレイを開く
    setIsEditing(true);
    setEditingInput('');
    setActiveOverlay('location');
  };

  const handleInputFocus = () => {
    // フォーカス時は常にLocationInfoOverlayを開く
    setActiveOverlay('location');
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      // 7桁未満で離脱した場合のみ編集モードを終了（displayText に自動復帰）。
      // 7桁入力済みの場合は fetch 中なので、currentRegNumber 変更時の
      // useEffect に任せる。
      if (isEditing && editingInput.length < 7) {
        setIsEditing(false);
      }
    }, 200);
  };

  const handleFavoritesClick = (e) => {
    e.stopPropagation();

    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (activeOverlay === 'favorites') {
      setActiveOverlay(null);
    } else {
      setActiveOverlay('favorites');
    }
  };

  // 表示用のプレースホルダーテキスト
  const getPlaceholder = () => {
    if (isEditing) {
      // 編集中は現在のエレベータ名/番号をプレースホルダーに表示
      return displayText || '登録番号を入力';
    }
    if (currentRegNumber) {
      return ''; // 値がある場合はプレースホルダー不要
    }
    return '登録番号を入力';
  };

  // 固定スタイルを適用
  const getContainerStyle = () => {
    if (!fixedDimensions) {
      return {};
    }

    return {
      position: 'fixed',
      width: 'calc(100vw/12*10)',
      height: `${fixedDimensions.height}px`,
      top: `${fixedDimensions.top}px`,
      left: 'calc(100vw/12)',
      zIndex: activeOverlay ? 50 : 10
    };
  };

  // ── input の value: 編集中はローカル値、それ以外は派生値 ──
  const inputValue = isEditing ? editingInput : displayText;

  return (
    <header
      className="h-[calc(100vh/72*7)] w-[calc(100vw/12*10)] flex items-center justify-between fixed left-[calc(100vw/12)]"
      style={{ zIndex: activeOverlay ? 50 : 10 }}
    >
      <div
        ref={containerRef}
        id="header-input-container"
        className="flex items-center w-full bg-white rounded-full cursor-pointer transition-all duration-300 shadow-lg"
        onClick={handleContainerClick}
        style={fixedDimensions ? getContainerStyle() : {
          height: 'calc(100vh/72*4)',
          marginTop: 'calc(100vh/24)'
        }}
      >
        <span className="pl-4 pr-3 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={getPlaceholder()}
          className="bg-transparent text-gray-800 font-semibold focus:outline-none w-full truncate pr-2"
          style={{ cursor: isEditing ? 'text' : 'pointer' }}
        />
        <button
          onClick={handleFavoritesClick}
          className="ml-3 mr-1 flex-shrink-0 bg-green-500 text-white p-1 rounded-full active:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300"
          id="favorites-button"
        >
          <img src="./assets/static/MEL_1.png" className="h-6 w-6" alt="favorites" />
        </button>
      </div>
    </header>
  );
};

export default Header;